import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import { StanTipTapEditor, StanToggle } from '@/components';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { useStoreSettings } from '../../queries/useStoreSettings';
import { DEFAULT_TERMS_AND_CONDITIONS } from '../../constants/store';
import { useDebounce } from '@/hooks/useDebounce';

export const TermsAndConditionsSection: React.FC = () => {
    const { user } = useUser();
    const [enabled, setEnabled] = useState(user?.data.terms_and_conditions?.enabled || false);
    const [content, setContent] = useState(user?.data.terms_and_conditions?.htmlText || DEFAULT_TERMS_AND_CONDITIONS);
    const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
    const [showSaved, setShowSaved] = useState(false);
    const { mutateAsync: updateStoreSettingsMutation, isPending } = useStoreSettings();

    // Track if user has made any edits (not just initialization)
    const hasUserEdited = useRef(false);
    const hasInitialized = useRef(false);

    // Debounce content with 1 second delay for better responsiveness
    const debouncedContent = useDebounce(content, 1000);

    // Only initialize from server data once on mount
    useEffect(() => {
        if (!hasInitialized.current && user) {
            setEnabled(user.data.terms_and_conditions?.enabled || false);
            setContent(user.data.terms_and_conditions?.htmlText || DEFAULT_TERMS_AND_CONDITIONS);
            hasInitialized.current = true;
        }
    }, [user]);

    const saveContent = useEffectEvent((htmlContent: string) => {
        updateSetting({ terms_and_conditions: htmlContent, terms_and_conditions_enabled: true });
    });

    // Save when debounced content changes (only if user has actually edited)
    useEffect(() => {
        if (hasUserEdited.current && debouncedContent) {
            saveContent(debouncedContent);
        }
    }, [debouncedContent]);

    const updateSetting = async (fields: Record<string, any>) => {
        // Check for placeholder text
        if (
            'terms_and_conditions' in fields &&
            typeof fields.terms_and_conditions === 'string' &&
            fields.terms_and_conditions.includes('(_____________)')
        ) {
            setFormErrors({
                termsAndCondition: ['Please update missing fields (_____________) with your own information'],
            });
            return;
        }

        try {
            await updateStoreSettingsMutation(fields);

            setShowSaved(true);
            setTimeout(() => {
                setShowSaved(false);
            }, 2000);

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Settings have been saved.',
            });
        } catch (error: any) {
            console.error('Settings update error:', error);

            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message || 'Please try again later.',
            });
        }
    };

    const handleToggleChange = (checked: boolean) => {
        setEnabled(checked);

        // If enabling, check for placeholder text immediately
        if (checked && content.includes('(_____________)')) {
            setFormErrors({
                termsAndCondition: ['Please update missing fields (_____________) with your own information'],
            });
            // Don't save to API if there are validation errors
            return;
        }

        setFormErrors({});

        // Match Vue behavior: send both fields together so the backend persists them correctly
        if (checked) {
            updateSetting({ terms_and_conditions_enabled: true, terms_and_conditions: content });
        } else {
            updateSetting({ terms_and_conditions_enabled: false });
        }
    };

    const handleContentChange = (newContent: string) => {
        // Mark that user has made an edit
        hasUserEdited.current = true;
        setContent(newContent);

        // Check for placeholder text immediately for instant feedback
        if (newContent.includes('(_____________)')) {
            setFormErrors({
                termsAndCondition: ['Please update missing fields (_____________) with your own information'],
            });
        } else {
            // Clear errors
            setFormErrors({});
        }
    };

    const handleRestoreDefault = () => {
        setContent(DEFAULT_TERMS_AND_CONDITIONS);
        hasUserEdited.current = true;
        setFormErrors({
            termsAndCondition: ['Please update missing fields (_____________) with your own information'],
        });
    };

    return (
        <div className=''>
            <div className='mb-6 max-w-3xl'>
                <div className='mb-2 flex items-center gap-2'>
                    <h4>Enable Terms & Conditions</h4>
                    <StanToggle
                        checked={enabled}
                        onChange={e => handleToggleChange(e.target.checked)}
                        disabled={isPending}
                    />
                    {isPending && <span className='text-caption-1-regular text-gray'>Saving...</span>}
                    {showSaved && !isPending && <span className='text-caption-1-regular text-gray'>Saved!</span>}
                </div>
                <p className='text-caption-1-regular text-gray'>
                    When enabled, customers are required to agree to the terms and conditions before the sale can be
                    completed. The agreement checkbox will appear on each checkout page.
                </p>
            </div>

            {enabled && (
                <div className='mt-3 max-w-5xl'>
                    {formErrors.termsAndCondition && formErrors.termsAndCondition.length > 0 && (
                        <p className='text-body-sm text-negative-fg-negative-bold mb-3'>
                            {formErrors.termsAndCondition[0]}
                        </p>
                    )}
                    <StanTipTapEditor
                        value={content}
                        onChange={handleContentChange}
                        className='h-96 overflow-y-auto'
                        imageUploadAllowed={false}
                        videoAllowed={false}
                        generateAiAllowed={false}
                        allowAddProductLink={false}
                    />
                    <div className='mt-4 flex justify-end'>
                        <button
                            type='button'
                            onClick={handleRestoreDefault}
                            className='text-label-sm-bold text-brand-main cursor-pointer'
                        >
                            Restore Default
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
